import { addDays, type ISODate } from './dates';
import type { DayCapacity } from './capacity';

/**
 * Notification panel. The notifications are not stored events: the server derives them from the data a person is
 * allowed to see anyway (own hours and absences, queues of an approver, month closing for those who close months,
 * announcements). Only the read marks and the filter of a person are stored.
 */
export const NOTIFICATION_CATEGORIES = ['hours', 'leave', 'approvals', 'period', 'missing', 'forma', 'announcement', 'system'] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_SEVERITIES = ['info', 'success', 'warning', 'critical'] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

/** Filter of a person (stored on the server, follows the person to every device). */
export interface NotificationFilter {
  /** categories the person does not want to see */
  hidden: NotificationCategory[];
  onlyUnread: boolean;
}

export const DEFAULT_NOTIFICATION_FILTER: NotificationFilter = { hidden: [], onlyUnread: false };

export function normalizeNotificationFilter(raw: unknown): NotificationFilter {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<NotificationFilter>;
  const hidden = Array.isArray(r.hidden) ? r.hidden.filter((c): c is NotificationCategory => (NOTIFICATION_CATEGORIES as readonly string[]).includes(c as string)) : [];
  return { hidden: [...new Set(hidden)], onlyUnread: r.onlyUnread === true };
}

export interface NotificationItem {
  /** stable key of the notification (changes when its content changes, so a changed state is unread again) */
  key: string;
  category: NotificationCategory;
  severity: NotificationSeverity;
  /** translation key below "notify." and its values; the web client writes the text */
  code: string;
  params: Record<string, string | number>;
  /** number of things it stands for (hours to correct, items to approve) */
  count: number;
  /** time of the event (ISO); state-like notifications carry the time of the calculation */
  at: string;
  /** route in the app */
  link: string;
  read: boolean;
  /** something the person can or must do (shown as a task on the start page) */
  actionable: boolean;
}

/** Order: unread first, then severity, then newest. */
export function compareNotifications(a: Pick<NotificationItem, 'read' | 'severity' | 'at'>, b: Pick<NotificationItem, 'read' | 'severity' | 'at'>): number {
  const sev = (x: NotificationSeverity) => NOTIFICATION_SEVERITIES.indexOf(x);
  return Number(a.read) - Number(b.read) || sev(b.severity) - sev(a.severity) || b.at.localeCompare(a.at);
}

/**
 * Working days of the last `lookbackDays` days without any booking: days before today with a target (no weekend,
 * holiday or absence), on or after the start of tracking.
 */
export function missingBookingDays(
  capacity: DayCapacity[],
  bookedByDate: ReadonlyMap<ISODate, number>,
  opts: { today: ISODate; lookbackDays: number; trackingStart: ISODate | null },
): ISODate[] {
  const from = addDays(opts.today, -Math.max(1, opts.lookbackDays));
  return capacity
    .filter((d) => d.date >= from && d.date < opts.today && (!opts.trackingStart || d.date >= opts.trackingStart) && d.targetMinutes > 0 && (bookedByDate.get(d.date) ?? 0) === 0)
    .map((d) => d.date)
    .sort();
}

export interface WeekState {
  weekStart: ISODate;
  /** entries of the week that are not submitted yet (draft or rejected) */
  openEntries: number;
}

/** Weeks whose submit deadline (end of week + submitDeadlineDays) is today or over while entries are still open. */
export function weekReminders(weeks: WeekState[], opts: { today: ISODate; submitDeadlineDays: number }): { weekStart: ISODate; deadline: ISODate; state: 'overdue' | 'due'; openEntries: number }[] {
  const out: { weekStart: ISODate; deadline: ISODate; state: 'overdue' | 'due'; openEntries: number }[] = [];
  for (const w of weeks) {
    if (w.openEntries <= 0) continue;
    const deadline = addDays(w.weekStart, 6 + opts.submitDeadlineDays);
    if (deadline > opts.today) continue;
    out.push({ weekStart: w.weekStart, deadline, state: deadline === opts.today ? 'due' : 'overdue', openEntries: w.openEntries });
  }
  return out.sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}
